import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  Steps,
  Space,
  Descriptions,
  Tag,

  message,
  DatePicker,
  Radio,
  Checkbox,
} from 'antd';
import {
  RetweetOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,

  UserOutlined,
  CalendarOutlined,
  StockOutlined,
} from '@ant-design/icons';
import type { OutboundOrder, ReturnInfo } from '../pages/OutboundOrderPage';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

interface ReturnOrderModalProps {
  visible: boolean;
  order: OutboundOrder | null;
  onCancel: () => void;
  onSubmit: (orderId: string, returnInfo: ReturnInfo) => void;
}

const ReturnOrderModal: React.FC<ReturnOrderModalProps> = ({
  visible,
  order,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [returnReason, setReturnReason] = useState('');
  const [handlingStatus, setHandlingStatus] = useState<'keep' | 'discard' | 'refund' | 'exchange'>('keep');
  
  // 商品選擇狀態
  const [selectedItems, setSelectedItems] = useState<{
    [itemId: string]: {
      selected: boolean;
      returnQuantity: number;
      reason: string;
      condition: string;
    }
  }>({});


  // 重置表單
  useEffect(() => {
    if (visible && order) {
      form.resetFields();
      setCurrentStep(0);
      setReturnReason('');
      setHandlingStatus('keep');
      
      // 重置商品選擇狀態，默認選擇蓝牙音箱
      const initialSelectedItems: typeof selectedItems = {};
      if (order) {
        order.items.forEach(item => {
          if (item.productNameEN.toLowerCase().includes('bluetooth speaker')) {
            initialSelectedItems[item.id] = {
              selected: true,
              returnQuantity: 1,
              reason: 'damaged_in_shipping',
              condition: 'damaged'
            };
          }
        });
      }
      setSelectedItems(initialSelectedItems);

      // 如果已有退貨信息，載入現有數據
      if (order.returnInfo) {
        const returnInfo = order.returnInfo;
        form.setFieldsValue({
          reason: returnInfo.reason,
          merchantConfirmation: returnInfo.merchantConfirmation,
          handlingStatus: returnInfo.handlingStatus,
          handlingRemark: returnInfo.handlingRemark,
        });
        setReturnReason(returnInfo.reason);
        setHandlingStatus(returnInfo.handlingStatus);
        
        // 設置適當的步驟
        if (returnInfo.confirmedDate) {
          setCurrentStep(2);
        } else if (returnInfo.merchantConfirmation) {
          setCurrentStep(1);
        }
      }
    }
  }, [visible, order, form]);

  // 處理步驟1：申請退貨
  const handleReturnRequest = async () => {
    try {
      // 檢查是否選擇了商品
      const selectedCount = getSelectedItemsCount();
      if (selectedCount === 0) {
        message.error('請至少選擇一個商品進行退貨');
        return;
      }

      const values = await form.validateFields(['reason']);
      setReturnReason(values.reason);
      setCurrentStep(1);
      message.success(`已選擇 ${selectedCount} 個商品，退款總額: ¥${getTotalRefundAmount().toFixed(2)}`);
    } catch (error) {
      console.error('表單驗證失敗:', error);
    }
  };

  // 處理步驟2：商家確認
  const handleMerchantConfirmation = async () => {
    try {
      const values = await form.validateFields(['merchantConfirmation', 'handlingStatus']);
      setHandlingStatus(values.handlingStatus);
      setCurrentStep(2);
    } catch (error) {
      console.error('表單驗證失敗:', error);
    }
  };

  // 處理商品選擇
  const handleItemSelection = (itemId: string, field: string, value: any) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
        // 如果取消選擇，重置為默認值
        ...(field === 'selected' && !value ? {
          returnQuantity: 1,
          reason: 'damaged_in_shipping',
          condition: 'damaged'
        } : {})
      }
    }));
  };

  // 獲取選中商品數量
  const getSelectedItemsCount = () => {
    return Object.values(selectedItems).filter(item => item.selected).length;
  };

  // 獲取總退貨數量
  const getTotalReturnQuantity = () => {
    return Object.values(selectedItems).reduce((total, item) => {
      return item.selected ? total + item.returnQuantity : total;
    }, 0);
  };

  // 獲取總退款金额
  const getTotalRefundAmount = () => {
    if (!order) return 0;
    return Object.entries(selectedItems).reduce((total, [itemId, itemData]) => {
      if (itemData.selected) {
        const orderItem = order.items.find(item => item.id === itemId);
        if (orderItem) {
          return total + (itemData.returnQuantity * orderItem.unitPrice);
        }
      }
      return total;
    }, 0);
  };

  // 最終提交
  const handleFinalSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const returnInfo: ReturnInfo = {
        requestDate: new Date().toISOString(),
        reason: values.reason,
        merchantConfirmation: values.merchantConfirmation,
        handlingStatus: values.handlingStatus,
        handlingRemark: values.handlingRemark,
        confirmedBy: '當前用戶', // 在實際應用中應該從用戶上下文獲取
        confirmedDate: new Date().toISOString(),
        inventoryAdjusted: values.inventoryAdjusted || false,
        adjustmentDate: values.inventoryAdjusted ? new Date().toISOString() : undefined,
      };

      if (order) {
        onSubmit(order.id, returnInfo);
        message.success('退貨處理完成');
        onCancel();
      }
    } catch (error) {
      console.error('提交失敗:', error);
    }
  };

  // 獲取處理狀態顏色
  const getHandlingStatusColor = (status: string) => {
    switch (status) {
      case 'keep':
        return 'green';
      case 'discard':
        return 'red';
      case 'refund':
        return 'orange';
      case 'exchange':
        return 'blue';
      default:
        return 'default';
    }
  };

  // 獲取處理狀態文本
  const getHandlingStatusText = (status: string) => {
    switch (status) {
      case 'keep':
        return '保留商品';
      case 'discard':
        return '丟棄商品';
      case 'refund':
        return '退款處理';
      case 'exchange':
        return '換貨處理';
      default:
        return '未知';
    }
  };

  const steps = [
    {
      title: '申請退貨',
      description: '填寫退貨原因',
      icon: <RetweetOutlined />,
    },
    {
      title: '商家確認',
      description: '聯繫商家確認處理方式',
      icon: <PhoneOutlined />,
    },
    {
      title: '完成處理',
      description: '確認處理結果',
      icon: <CheckCircleOutlined />,
    },
  ];

  if (!order) {
    return null;
  }

  return (
    <Modal
      title={
        <Space>
          <RetweetOutlined />
          退貨處理 - {order.orderNumber}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Alert
        message="退貨處理流程"
        description="請按照步驟完成退貨處理：1) 填寫退貨原因 → 2) 聯繫商家確認處理方式 → 3) 根據確認結果完成處理"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map((step, index) => (
          <Step
            key={index}
            title={step.title}
            description={step.description}
            icon={step.icon}
          />
        ))}
      </Steps>

      {/* 訂單基本信息 */}
      <Card title="訂單信息" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="訂單號">{order.orderNumber}</Descriptions.Item>
          <Descriptions.Item label="客戶">{order.customerName}</Descriptions.Item>
          <Descriptions.Item label="收件人">{order.receiver}</Descriptions.Item>
          <Descriptions.Item label="當前狀態">
            <Tag color="green">{order.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="訂單金額">¥{order.totalValue.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="創建時間">
            {dayjs(order.createdAt).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Form form={form} layout="vertical">
        {/* 步驟1: 申請退貨 */}
        {currentStep === 0 && (
          <Card title="步驟1: 申請退貨" style={{ marginBottom: 16 }}>
            <Form.Item
              label="退貨原因"
              name="reason"
              rules={[{ required: true, message: '請填寫退貨原因' }]}
            >
              <Select placeholder="請選擇退貨原因">
                <Option value="質量問題">質量問題</Option>
                <Option value="尺寸不符">尺寸不符</Option>
                <Option value="顏色不符">顏色不符</Option>
                <Option value="功能故障">功能故障</Option>
                <Option value="發錯商品">發錯商品</Option>
                <Option value="包裝破損">包裝破損</Option>
                <Option value="客戶不滿意">客戶不滿意</Option>
                <Option value="其他原因">其他原因</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="詳細說明"
              name="reasonDetail"
            >
              <TextArea 
                rows={3} 
                placeholder="請詳細描述退貨原因..."
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Row justify="end">
              <Space>
                <Button onClick={onCancel}>取消</Button>
                <Button type="primary" onClick={handleReturnRequest}>
                  下一步
                </Button>
              </Space>
            </Row>
          </Card>
        )}

        {/* 步驟2: 商家確認 */}
        {currentStep === 1 && (
          <Card title="步驟2: 商家確認" style={{ marginBottom: 16 }}>
            <Alert
              message="聯繫商家確認"
              description={
                <div>
                  <p>退貨原因: <Text strong>{returnReason}</Text></p>
                  <p>請致電商家確認處理方式，並在下方記錄確認結果。</p>
                </div>
              }
              type="warning"
              showIcon
              icon={<PhoneOutlined />}
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="商家確認記錄"
              name="merchantConfirmation"
              rules={[{ required: true, message: '請填寫商家確認記錄' }]}
            >
              <TextArea 
                rows={3} 
                placeholder="請記錄與商家溝通的確認結果..."
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item
              label="處理方式"
              name="handlingStatus"
              rules={[{ required: true, message: '請選擇處理方式' }]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="keep">
                    <Space>
                      <StockOutlined style={{ color: '#52c41a' }} />
                      <div>
                        <div>保留商品 (需補回庫存)</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          商品完好，可以重新銷售
                        </Text>
                      </div>
                    </Space>
                  </Radio>
                  <Radio value="discard">
                    <Space>
                      <WarningOutlined style={{ color: '#ff4d4f' }} />
                      <div>
                        <div>丟棄商品</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          商品損壞，無法重新銷售
                        </Text>
                      </div>
                    </Space>
                  </Radio>
                  <Radio value="refund">
                    <Space>
                      <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                      <div>
                        <div>退款處理</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          退還客戶款項
                        </Text>
                      </div>
                    </Space>
                  </Radio>
                  <Radio value="exchange">
                    <Space>
                      <RetweetOutlined style={{ color: '#1890ff' }} />
                      <div>
                        <div>換貨處理</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          為客戶更換商品
                        </Text>
                      </div>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Row justify="space-between">
              <Button onClick={() => setCurrentStep(0)}>上一步</Button>
              <Space>
                <Button onClick={onCancel}>取消</Button>
                <Button type="primary" onClick={handleMerchantConfirmation}>
                  下一步
                </Button>
              </Space>
            </Row>
          </Card>
        )}

        {/* 步驟3: 完成處理 */}
        {currentStep === 2 && (
          <Card title="步驟3: 完成處理" style={{ marginBottom: 16 }}>
            <Alert
              message="處理確認"
              description={
                <div>
                  <p>處理方式: <Tag color={getHandlingStatusColor(handlingStatus)}>
                    {getHandlingStatusText(handlingStatus)}
                  </Tag></p>
                  {handlingStatus === 'keep' && (
                    <p style={{ color: '#fa8c16' }}>
                      <WarningOutlined /> 選擇保留商品需要將數量補回庫存
                    </p>
                  )}
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="處理備註"
              name="handlingRemark"
            >
              <TextArea 
                rows={3} 
                placeholder="請填寫處理過程的備註信息..."
                maxLength={500}
                showCount
              />
            </Form.Item>

            {handlingStatus === 'keep' && (
              <Form.Item
                name="inventoryAdjusted"
                valuePropName="checked"
              >
                <Checkbox>
                  <Space>
                    <StockOutlined />
                    我已完成庫存調整，將退回的商品數量補回庫存
                  </Space>
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item
              label="處理人員"
              name="handledBy"
              initialValue="當前用戶"
            >
              <Input disabled prefix={<UserOutlined />} />
            </Form.Item>

            <Form.Item
              label="處理時間"
              name="handledDate"
              initialValue={dayjs()}
            >
              <DatePicker 
                showTime 
                disabled 
                style={{ width: '100%' }}
                prefix={<CalendarOutlined />}
              />
            </Form.Item>

            <Row justify="space-between">
              <Button onClick={() => setCurrentStep(1)}>上一步</Button>
              <Space>
                <Button onClick={onCancel}>取消</Button>
                <Button 
                  type="primary" 
                  onClick={handleFinalSubmit}
                  disabled={handlingStatus === 'keep' && !form.getFieldValue('inventoryAdjusted')}
                >
                  完成退貨處理
                </Button>
              </Space>
            </Row>
          </Card>
        )}
      </Form>

      {/* 商品清單 */}
      <Card title="退貨商品清單" size="small">
        {currentStep === 0 && (
          <Alert
            message="部分退貨說明"
            description="您可以選擇只退回部分商品和數量。例如：從5個蓝牙音箱中只退回1個損壞的商品。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        <div>
          {order.items.map((item, index) => (
            <div key={item.id} style={{ 
              marginBottom: index < order.items.length - 1 ? 16 : 0,
              padding: 12,
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              backgroundColor: '#fafafa'
            }}>
              {/* 商品選擇 */}
              <Row align="middle" style={{ marginBottom: 12 }}>
                <Col span={1}>
                  <Checkbox
                    checked={selectedItems[item.id]?.selected || false}
                    onChange={(e) => handleItemSelection(item.id, 'selected', e.target.checked)}
                  />
                </Col>
                <Col span={23}>
                  <div style={{ fontWeight: 500 }}>{item.productNameEN}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {item.productNameCN} | SKU: {item.skuId}
                  </div>
                </Col>
              </Row>

              {/* 退貨詳情 */}
              {selectedItems[item.id]?.selected && (
                <div style={{ 
                  marginTop: 12, 
                  padding: 12, 
                  backgroundColor: '#fff',
                  border: '1px solid #e6f7ff',
                  borderRadius: 4
                }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>退貨數量</Text>
                      </div>
                      <Input
                        type="number"
                        min={1}
                        max={item.quantityBaseUnit}
                        value={selectedItems[item.id]?.returnQuantity || 1}
                        onChange={(e) => handleItemSelection(item.id, 'returnQuantity', parseInt(e.target.value) || 1)}
                        addonAfter={`/ ${item.quantityBaseUnit}`}
                      />
                    </Col>
                    <Col span={8}>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>退貨原因</Text>
                      </div>
                      <Select
                        style={{ width: '100%' }}
                        value={selectedItems[item.id]?.reason || 'damaged_in_shipping'}
                        onChange={(value) => handleItemSelection(item.id, 'reason', value)}
                      >
                        <Option value="damaged_in_shipping">運送損壞</Option>
                        <Option value="defective_product">產品缺陷</Option>
                        <Option value="wrong_item_sent">發錯商品</Option>
                        <Option value="not_as_described">與描述不符</Option>
                        <Option value="changed_mind">改變主意</Option>
                        <Option value="other">其他原因</Option>
                      </Select>
                    </Col>
                    <Col span={6}>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>商品狀態</Text>
                      </div>
                      <Select
                        style={{ width: '100%' }}
                        value={selectedItems[item.id]?.condition || 'damaged'}
                        onChange={(value) => handleItemSelection(item.id, 'condition', value)}
                      >
                        <Option value="new">全新</Option>
                        <Option value="like_new">近似全新</Option>
                        <Option value="damaged">損壞</Option>
                        <Option value="defective">有缺陷</Option>
                        <Option value="unusable">無法使用</Option>
                      </Select>
                    </Col>
                    <Col span={4}>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>退款金額</Text>
                      </div>
                      <Tag color="red" style={{ fontSize: '14px', padding: '4px 8px' }}>
                        ¥{((selectedItems[item.id]?.returnQuantity || 1) * item.unitPrice).toFixed(2)}
                      </Tag>
                    </Col>
                  </Row>
                </div>
              )}

              {/* 原始商品信息 */}
              {!selectedItems[item.id]?.selected && (
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text type="secondary">原訂購數量: {item.quantityBaseUnit}</Text>
                  </Col>
                  <Col>
                    <Tag>總價: ¥{(item.unitPrice * item.quantityBaseUnit).toFixed(2)}</Tag>
                  </Col>
                </Row>
              )}
            </div>
          ))}

          {/* 退貨總計 */}
          {getSelectedItemsCount() > 0 && (
            <div style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 4
            }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>
                    已選擇 {getSelectedItemsCount()} 個商品，
                    退貨數量總計: {getTotalReturnQuantity()}
                  </Text>
                </Col>
                <Col>
                  <Text strong style={{ color: '#f5222d', fontSize: '16px' }}>
                    退款總額: ¥{getTotalRefundAmount().toFixed(2)}
                  </Text>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Card>
    </Modal>
  );
};

export default ReturnOrderModal;